import { useEffect, useMemo, useState } from 'react'

import * as publicacionesService from '../components/servicios/publicacionesService'
import { esCobroPorPublicar } from '../constants/publicaciones.js'
import { useDebounce } from './useDebounce.js'

const DEBOUNCE_PRECIO_MS = 900

/**
 * Trae las exposiciones disponibles con su costo ya calculado por el backend y
 * deriva el aviso de la exposición seleccionada.
 *
 * @param {object} params
 * @param {number} params.idCategoria
 * @param {boolean} params.esScrap
 * @param {number} params.precioUnitario
 * @param {number} params.alicuota
 * @param {number} params.idSeleccionada
 * @returns {{exposiciones: Array, aviso: {monto: number, fechaVencimiento: string|null}|null,
 *   fechaVencimientoPrevista: string|null, esCobroPorPublicar: boolean}}
 *   `aviso` y `esCobroPorPublicar` dependen de la exposición seleccionada;
 */
export const useExposiciones = ({
  idCategoria,
  esScrap,
  precioUnitario,
  alicuota,
  idSeleccionada
}) => {
  const [exposiciones, setExposiciones] = useState([])
  const [cargando, setCargando] = useState(true)
  const precioUnitarioDebounceado = useDebounce(precioUnitario, DEBOUNCE_PRECIO_MS)

  const obtenerExposiciones = async (estaVigente) => {
    const exposicionRtn = await publicacionesService.buscarExposicion('', {
      idCategoria,
      esScrap,
      baseCalculo: precioUnitarioDebounceado,
      alicuota
    })

    if (estaVigente()) {
      setExposiciones(Array.isArray(exposicionRtn?.data) ? exposicionRtn.data : [])
      setCargando(false)
    }
  }

  useEffect(() => {
    if (precioUnitarioDebounceado === undefined || precioUnitarioDebounceado === null) {
      return
    }

    let cancelado = false

    obtenerExposiciones(() => !cancelado)

    return () => {
      cancelado = true
    }
  }, [precioUnitarioDebounceado, idCategoria, esScrap, alicuota])

  const seleccionada = useMemo(
    () => exposiciones.find((e) => e.idPublicacionExposicion === Number(idSeleccionada)),
    [exposiciones, idSeleccionada]
  )

  const esSeleccionadaCobroPorPublicar = Boolean(seleccionada && esCobroPorPublicar(seleccionada))

  const aviso = useMemo(() => {
    if (!esSeleccionadaCobroPorPublicar) {
      return null
    }

    return {
      monto: Number(seleccionada.montoCalculado) || 0,
      fechaVencimiento: seleccionada.fechaVencimientoCalculada || null
    }
  }, [esSeleccionadaCobroPorPublicar, seleccionada])

  const fechaVencimientoPrevista = useMemo(() => {
    const conAviso = exposiciones.find((e) => esCobroPorPublicar(e) && e.fechaVencimientoCalculada)

    return conAviso?.fechaVencimientoCalculada || null
  }, [exposiciones])

  return {
    exposiciones,
    aviso,
    cargando,
    fechaVencimientoPrevista,
    esCobroPorPublicar: esSeleccionadaCobroPorPublicar
  }
}
